export type AssetPanelContext =
  | { kind: 'overview'; assetId: string | number }
  | { kind: 'vulnerability'; assetId: string | number; vulnerabilityId?: string; label?: string }
  | { kind: 'control'; assetId: string | number; controlLinkId?: string; label?: string }
  | { kind: 'risk'; assetId: string | number; sourceType?: string; sourceId?: string; label?: string }
  | { kind: 'evidence'; assetId: string | number }
  | { kind: 'remediation'; assetId: string | number }
  | { kind: 'scans'; assetId: string | number }
  | { kind: 'relationships'; assetId: string | number }
  | { kind: 'graph'; assetId: string | number }
  | { kind: 'timeline'; assetId: string | number }
  | { kind: 'fullPassport'; assetId: string | number }

export type AssetContextNavKind = Exclude<AssetPanelContext['kind'], 'fullPassport'>

export const ASSET_CONTEXT_NAV_ITEMS: AssetContextNavKind[] = [
  'overview',
  'vulnerability',
  'control',
  'risk',
  'evidence',
  'remediation',
  'scans',
  'relationships',
  'graph',
  'timeline',
]

export function assetContextNavLabel(
  kind: AssetContextNavKind,
  t: (key: string, fallback?: string) => string
): string {
  const map: Record<AssetContextNavKind, { key: string; fallback: string }> = {
    overview: { key: 'assets.assetPassport.overview', fallback: 'Overview' },
    vulnerability: { key: 'assets.assetPassport.vulnerabilities', fallback: 'Vulnerabilities' },
    control: { key: 'assets.assetPassport.controls', fallback: 'Controls' },
    risk: { key: 'assets.assetPassport.risks', fallback: 'Risks' },
    evidence: { key: 'assets.assetPassport.evidence', fallback: 'Evidence' },
    remediation: { key: 'assets.assetPassport.remediation', fallback: 'Remediation' },
    scans: { key: 'assets.assetPassport.scans', fallback: 'Scans' },
    relationships: { key: 'assets.assetPassport.relationships', fallback: 'Relationships' },
    graph: { key: 'assets.assetPassport.graph', fallback: 'Graph' },
    timeline: { key: 'assets.assetPassport.timeline', fallback: 'Timeline' },
  }
  const entry = map[kind]
  return t(entry.key, entry.fallback)
}

export function assetPanelContextTitle(
  ctx: AssetPanelContext,
  t: (key: string, fallback?: string) => string
): string {
  if (ctx.kind === 'fullPassport') {
    return t('assets.assetPassport.fullPassport', 'Full asset passport')
  }
  if (ctx.label && (ctx.kind === 'vulnerability' || ctx.kind === 'control' || ctx.kind === 'risk')) {
    return ctx.label
  }
  return assetContextNavLabel(ctx.kind as AssetContextNavKind, t)
}

export function assetPanelContextFor(
  assetId: string | number,
  kind: AssetContextNavKind | 'fullPassport'
): AssetPanelContext {
  if (kind === 'fullPassport') return { kind: 'fullPassport', assetId }
  return { kind, assetId }
}

export function switchAssetContextKind(
  current: AssetPanelContext,
  kind: AssetContextNavKind
): AssetPanelContext {
  const assetId = current.assetId
  return { kind, assetId }
}

export function assetDrillDownToContext(target: {
  kind: 'vulnerability' | 'control' | 'risk'
  assetId: string | number
  id?: string
  label?: string
  sourceType?: string
  sourceId?: string
}): AssetPanelContext {
  if (target.kind === 'vulnerability') {
    return { kind: 'vulnerability', assetId: target.assetId, vulnerabilityId: target.id, label: target.label }
  }
  if (target.kind === 'control') {
    return { kind: 'control', assetId: target.assetId, controlLinkId: target.id, label: target.label }
  }
  return {
    kind: 'risk',
    assetId: target.assetId,
    sourceType: target.sourceType,
    sourceId: target.sourceId || target.id,
    label: target.label,
  }
}
